import React, { memo, Fragment } from 'react';
import { View } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  Divider,
  LayoutGrid,
  LoadingContainer,
  Space,
  Text,
  IconCountry,
  IconBrowser,
  IconOS,
  IconDevice,
} from '@redlink/components/atoms';
import {
  StatTileItem,
  LastActivityStatItem,
  MultiProgress,
} from '@redlink/components/molecules';
import { useLocale } from '@redlink/assets/locale';
import { useQuery, getPageVisitorsDetail } from '@redlink/services';

export const VisitorBlock = memo(() => {
  const { styles } = useStyle();
  const { dictionary } = useLocale();
  const {
    loading,
    data: {
      PageVisitorsDetail: {
        countries = [],
        browsers = [],
        os = [],
        devices = [],
        refers = [],
        lastActivities = [],
      } = {},
    },
  } = useQuery(getPageVisitorsDetail);

  return (
    <View>
      <LoadingContainer isBusy={loading}>
        <LayoutGrid>
          <View style={styles.statsBlockContainer}>
            <CountriesBlock data={countries} />
            <Divider horizontal={false} margin={20} />
            <BrowsersBlock data={browsers} />
          </View>
          <View>
            <OsStatsBlock data={os} title={dictionary.os} />
            <Space />
            <DevicesStatsBlock data={devices} title={dictionary.devices} />
          </View>
        </LayoutGrid>
        <Divider margin={16} />
        <LayoutGrid>
          <RefersBlock data={refers} />
          <LastActivitiesBlock data={lastActivities} />
        </LayoutGrid>
      </LoadingContainer>
    </View>
  );
});

const StatBlockContainer = memo(({ title, children }) => {
  const { styles } = useStyle();
  return (
    <View style={styles.statsBlockRootContainer}>
      <Text style={styles.statBlockTitleText}>{title}</Text>
      <Space size={20} />
      {children}
    </View>
  );
});

const OsStatsBlock = memo(({ title, data }) => {
  const osColors = {
    Android: '#8bc34a',
    iOS: '#455a64',
    Windows: '#00bcd4',
    'Mac OS': '#1F6EEC',
    Linux: '#4A0D1D',
    Other: '#cccccc',
  };

  const normalizedData = data.map((item) => {
    return {
      title: item.title,
      value: item.value,
      color: osColors[item.title],
      logo: <IconOS code={item.title} color={osColors[item.title]} />,
    };
  });
  return (
    <StatBlockContainer {...{ title }}>
      <MultiProgress data={normalizedData} />
    </StatBlockContainer>
  );
});

const DevicesStatsBlock = memo(({ title, data }) => {
  const osColors = {
    Mobile: '#00bcd4',
    Desktop: '#0283ff',
  };

  const normalizedData = data.map((item) => {
    return {
      title: item.title,
      value: item.value,
      color: osColors[item.title],
      logo: <IconDevice code={item.title} color={osColors[item.title]} />,
    };
  });
  return (
    <StatBlockContainer {...{ title }}>
      <MultiProgress data={normalizedData} />
    </StatBlockContainer>
  );
});

const StatsBlock = memo(({ title, data, renderItem }) => {
  return (
    <StatBlockContainer {...{ title }}>
      <View>
        {data.map((item, index) => {
          return (
            <Fragment key={index}>
              {renderItem?.(item)}
              <Space />
            </Fragment>
          );
        })}
      </View>
    </StatBlockContainer>
  );
});

const LastActivitiesBlock = memo(({ data }) => {
  const { dictionary } = useLocale();
  return (
    <StatsBlock
      title={dictionary.lastActivities}
      {...{ data }}
      renderItem={(item) => <LastActivityStatItem {...item} />}
    />
  );
});

const CountriesBlock = memo(({ data }) => {
  const { dictionary } = useLocale();
  return (
    <StatsBlock
      title={dictionary.countries}
      {...{ data }}
      renderItem={({ code, title, value }) => (
        <StatTileItem
          {...{ title: code, value }}
          IconComponent={<IconCountry {...{ code, title }} />}
        />
      )}
    />
  );
});

const BrowsersBlock = memo(({ data }) => {
  const { dictionary } = useLocale();
  return (
    <StatsBlock
      title={dictionary.browsers}
      {...{ data }}
      renderItem={({ code, title, value }) => (
        <StatTileItem
          {...{ title, value }}
          IconComponent={<IconBrowser code={title} />}
        />
      )}
    />
  );
});

const RefersBlock = memo(({ data }) => {
  const { dictionary } = useLocale();
  return (
    <StatsBlock
      title={dictionary.refers}
      {...{ data }}
      renderItem={({ code, title, value }) => (
        <StatTileItem {...{ title, value }} />
      )}
    />
  );
});

const useStyle = createUseStyle(({ colors }) => ({
  colorGuidContainer: {
    paddingHorizontal: 15,
  },
  statBlockTitleText: {
    color: colors.gray21,
    fontSize: 14,
  },
  statsBlockRootContainer: {
    // flex: 1,
  },
  statsBlockContainer: {
    flex: 1,
    flexDirection: 'row',
  },
}));
